import React, { useState, useRef } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useUploadMediaAssetMutation } from '../../store/services/whatsappApi';

interface MediaUploaderProps {
  onMediaUploaded: (mediaHandle: string, mediaType: string) => void;
  mediaType?: string;
  className?: string;
  useWhatsAppApi?: boolean;
  phoneNumberId?: string;
}

const MediaUploader: React.FC<MediaUploaderProps> = ({ 
  onMediaUploaded, 
  mediaType = 'image',
  className = '',
  useWhatsAppApi = false,
  phoneNumberId = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMediaAsset] = useUploadMediaAssetMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showNotification } = useNotification();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    let fileType = mediaType;
    if (file.type.startsWith('image/')) {
      fileType = 'image';
    } else if (file.type.startsWith('video/')) {
      fileType = 'video';
    } else if (file.type.startsWith('application/') || file.type.startsWith('text/')) {
      fileType = 'document';
    } else {
      showNotification('error', `Unsupported file type: ${file.type}`);
      return;
    }

    // Validate file size (16MB max for WhatsApp)
    const maxSize = 16 * 1024 * 1024; // 16MB in bytes
    if (file.size > maxSize) {
      showNotification('error', 'File size exceeds the 16MB limit');
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(10);

      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', fileType);
      
      // Add WhatsApp API parameters if needed
      if (useWhatsAppApi) {
        formData.append('use_whatsapp_api', 'true');
        if (phoneNumberId) {
          formData.append('phone_number_id', phoneNumberId);
        } else {
          showNotification('error', 'Phone number ID is required for WhatsApp API uploads');
          setIsUploading(false);
          setUploadProgress(0);
          return;
        }
      }

      // Simulate progress (since we don't have real progress events)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 500);

      // Upload the file
      const result = await uploadMediaAsset(formData).unwrap();
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Call the callback with the media handle
      if (result && result.media_handle) {
        onMediaUploaded(result.media_handle, fileType);
        showNotification('success', 'Media uploaded successfully');
      } else {
        showNotification('error', 'Failed to get media handle from upload');
      }
    } catch (error: any) {
      console.error('Error uploading media:', error);
      showNotification('error', `Upload failed: ${error?.data?.detail || error?.message || 'Unknown error'}`);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={`media-uploader ${className}`}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept={
          mediaType === 'image' ? 'image/*' :
          mediaType === 'video' ? 'video/*' :
          mediaType === 'document' ? '.pdf,.doc,.docx,.txt' : '*'
        }
      />
      
      <button
        type="button"
        onClick={handleButtonClick}
        disabled={isUploading}
        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md flex items-center"
      >
        {isUploading ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Uploading ({uploadProgress}%)
          </>
        ) : (
          <>
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            Upload {mediaType.charAt(0).toUpperCase() + mediaType.slice(1)}
          </>
        )}
      </button>
    </div>
  );
};

export default MediaUploader;
